/**
 * Typed client for the SwarmAI Hub API.
 *
 * Every request is same-origin (the server serves this SPA, or the Vite
 * dev proxy forwards /api) and sends cookies via `credentials: 'include'`
 * so the `sai_sess` session is attached automatically.
 */

export type Scope = 'read' | 'publish' | 'manage:packages';
export type Role = 'user' | 'admin';
export type Visibility = 'public' | 'private';

export interface User {
  email: string;
  displayName: string | null;
  github: string | null;
  canPublish: boolean;
  role: Role;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface TokenSummary {
  id: string;
  name: string;
  prefix: string;
  scopes: Scope[];
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}

export interface CreatedToken extends TokenSummary {
  token: string;
  warning: string;
}

export interface PackageSummary {
  id: string;
  name: string;
  kind: string;
  trust: string;
  description: string;
  tags: string[];
  authorGithub: string;
  latestVersion: string;
  downloadCount: number;
  installCount: number;
  ratingAvg: number | null;
  ratingCount: number;
  origin: 'repo' | 'api';
  visibility: Visibility;
}

export interface Install {
  installIdHash: string;
  deviceLabel: string | null;
  osHint: string | null;
  hostnameHint: string | null;
  linkedAt: string;
  lastSeenAt: string | null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const json = text ? safeParse(text) : null;
  if (!res.ok) {
    const j = json as { error?: string; message?: string } | null;
    throw new ApiError(res.status, j?.error ?? `http_${res.status}`, j?.message ?? res.statusText, json);
  }
  return json as T;
}

function safeParse(t: string): unknown {
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

export const api = {
  // ---- auth ----
  startEmail: (email: string, intent: 'login' | 'link-install' = 'login', installIdHash?: string) =>
    request<{ ok: true; ttl_seconds: number }>('POST', '/api/v1/auth/email/start', {
      email,
      intent,
      install_id_hash: installIdHash,
    }),
  logout: () => request<{ ok: true }>('POST', '/api/v1/auth/logout'),
  githubLinkUrl: (returnTo = '/dashboard/settings') =>
    `/api/v1/auth/github/start?return_to=${encodeURIComponent(returnTo)}`,

  // ---- me ----
  me: () => request<{ user: User }>('GET', '/api/v1/me').then((r) => r.user),
  updateProfile: (displayName: string | null) =>
    request<{ ok: true; user: User }>('PATCH', '/api/v1/me', { display_name: displayName }),
  myPackages: () => request<{ packages: PackageSummary[] }>('GET', '/api/v1/me/packages').then((r) => r.packages),
  installs: () => request<{ installs: Install[] }>('GET', '/api/v1/me/installs').then((r) => r.installs),
  setDeviceLabel: (hash: string, label: string | null) =>
    request<{ ok: true }>('PATCH', `/api/v1/me/installs/${hash}`, { device_label: label }),
  unlinkDevice: (hash: string) => request<{ ok: true }>('DELETE', `/api/v1/me/installs/${hash}`),

  // ---- tokens ----
  tokens: () => request<{ tokens: TokenSummary[] }>('GET', '/api/v1/me/tokens').then((r) => r.tokens),
  createToken: (name: string, scopes: Scope[], expiresInDays?: number) =>
    request<CreatedToken>('POST', '/api/v1/me/tokens', {
      name,
      scopes,
      expires_in_days: expiresInDays,
    }),
  revokeToken: (id: string) => request<{ ok: true }>('DELETE', `/api/v1/me/tokens/${id}`),

  // ---- packages / publish ----
  publish: (manifest: Record<string, unknown>, visibility: Visibility) =>
    request<{ ok: true; created: boolean; package: PackageSummary }>('POST', '/api/v1/publish', {
      manifest,
      visibility,
    }),
  setVisibility: (id: string, visibility: Visibility) =>
    request<{ ok: true }>('PATCH', `/api/v1/publish/${encodeURIComponent(id)}`, { visibility }),
  yank: (id: string) => request<{ ok: true }>('DELETE', `/api/v1/publish/${encodeURIComponent(id)}`),

  // ---- discovery ----
  discovery: () => request<DiscoveryDoc>('GET', '/api/v1'),

  // ---- admin ----
  adminUsers: (q?: string) =>
    request<{ total: number; users: User[] }>('GET', `/api/v1/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  adminUpdateUser: (email: string, patch: { can_publish?: boolean; role?: Role }) =>
    request<{ ok: true; user: User }>('PATCH', `/api/v1/admin/users/${encodeURIComponent(email)}`, patch),
};

export interface DiscoveryDoc {
  service: string;
  apiVersion: string;
  auth: { availableScopes: Scope[] };
  packaging: {
    kinds: string[];
    trustTiers: Record<string, string>;
    manifest: { requiredFields: string[]; idPattern: string };
  };
}
